import { initializeApp, getApps, getApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
  onAuthStateChanged as fbOnAuthStateChanged,
  onIdTokenChanged as fbOnIdTokenChanged,
} from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { IS_PROTOTYPE } from '@hapana/shared/web/services/prototype-mock/config'

let auth
let db
let app
let googleProvider

if (IS_PROTOTYPE) {
  // Prototype shell: no real Firebase project. Fake an always-signed-in staff user so
  // useAuthInitialize resolves instantly and every protected route renders.
  const fakeUser = {
    uid: 'prototype-staff-uid',
    email: 'demo.staff@hapana.com',
    displayName: 'Demo Staff',
    emailVerified: true,
    isAnonymous: false,
    providerData: [],
    getIdToken: async () => 'prototype-fake-token',
    getIdTokenResult: async () => ({ token: 'prototype-fake-token', claims: {} }),
    reload: async () => {},
  }
  auth = {
    currentUser: fakeUser,
    tenantId: null,
    signOut: async () => {},
    onAuthStateChanged: (cb) => {
      Promise.resolve().then(() => cb(fakeUser))
      return () => {}
    },
    onIdTokenChanged: (cb) => {
      Promise.resolve().then(() => cb(fakeUser))
      return () => {}
    },
  }
  db = {}
  app = {}
  googleProvider = { addScope: () => {}, setCustomParameters: () => {} }
} else {
  const requiredEnvVars = {
    VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
    VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
  }

  const missingVars = Object.entries(requiredEnvVars)
    .filter(([, value]) => !value)
    .map(([key]) => key)

  if (missingVars.length > 0) {
    throw new Error(
      `Missing required Firebase environment variables: ${missingVars.join(', ')}. ` +
        'Please ensure these are set in your .env file with VITE_ prefix and restart the Vite dev server.'
    )
  }

  const firebaseConfig = {
    apiKey: requiredEnvVars.VITE_FIREBASE_API_KEY,
    authDomain: requiredEnvVars.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: requiredEnvVars.VITE_FIREBASE_PROJECT_ID,
    storageBucket: requiredEnvVars.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: requiredEnvVars.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: requiredEnvVars.VITE_FIREBASE_APP_ID,
  }

  app = !getApps().length ? initializeApp(firebaseConfig) : getApp()
  auth = getAuth(app)
  db = getFirestore(app)

  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.error('Failed to set Firebase auth persistence:', error)
  })

  googleProvider = new GoogleAuthProvider()
  googleProvider.addScope('email')
  googleProvider.addScope('profile')
  googleProvider.setCustomParameters({
    prompt: 'select_account', // Always show account selection
  })

  // Connect to auth emulator in development if configured
  if (import.meta.env.MODE === 'development' && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true') {
    const emulatorUrl = import.meta.env.VITE_AUTH_EMULATOR_URL || 'http://localhost:9099'
    console.log('Connecting to Firebase Auth Emulator at:', emulatorUrl)
    connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true })
  }
}

export { auth, db }

/**
 * Configure auth instance for multi-tenancy
 * @param {string} tenantId - The Google Identity Platform tenant ID
 */
export const setAuthTenant = (tenantId) => {
  if (IS_PROTOTYPE) return // no real tenant pool in prototype
  auth.tenantId = tenantId || null
}

export { googleProvider }

// Prototype-aware auth subscriptions: fire once with the fake user, else delegate to Firebase.
export const onAuthStateChanged = (authArg, cb) =>
  IS_PROTOTYPE
    ? (Promise.resolve().then(() => cb(authArg?.currentUser ?? null)), () => {})
    : fbOnAuthStateChanged(authArg, cb)

export const onIdTokenChanged = (authArg, cb) =>
  IS_PROTOTYPE
    ? (Promise.resolve().then(() => cb(authArg?.currentUser ?? null)), () => {})
    : fbOnIdTokenChanged(authArg, cb)

export default app
